import { HttpClient } from '@adapters/HttpClient';
import {
  ImportHistoryDetail,
  ImportHistoryItem,
  ImportHistoryPage,
} from '../models/ImportHistoryModel';

/**
 * Meta de paginación que devuelve la API (data.meta).
 * La API puede devolver snake_case: current_page, per_page, total, last_page.
 */
interface ImportHistoryResponseMeta {
  total: number;
  per_page?: number;
  perPage?: number;
  current_page?: number;
  currentPage?: number;
  last_page?: number;
  lastPage?: number;
}

/** Columnas permitidas para ordenación según API (sorting[column]). */
export const IMPORT_HISTORY_SORT_COLUMNS = [
  'id',
  'title',
  'description',
  'frequency',
  'expires_at',
  'user_id',
  'team_id',
  'status',
  'created_at',
  'updated_at',
] as const;

export interface ImportHistoryFilters {
  page?: number;
  /** Elementos por página. API: default 15, min 15, max 100 */
  per_page?: number;
  /** Columna de ordenación. API: sorting[column] */
  sort_column?: string;
  /** Dirección. API: sorting[direction]. Valores: asc, desc */
  sort_direction?: 'asc' | 'desc';
  searchTerm?: string;
  advanced_filters?: {
    filters: { column: string; operator: string; value?: string | string[] }[];
    logic: 'AND' | 'OR';
  };
}

export class ImportHistoryApiAdapter {
  constructor(private httpClient: HttpClient) {}

  /**
   * Obtiene el listado paginado del histórico de importaciones.
   * GET /api/v1/shops/import-history
   */
  async getImportHistoryPaginated(
    filters: ImportHistoryFilters = {}
  ): Promise<ImportHistoryPage> {
    const {
      page = 1,
      per_page = 15,
      sort_column = 'id',
      sort_direction = 'desc',
      searchTerm = '',
      advanced_filters,
    } = filters;

    // API: per_page min 15, max 100
    const perPageClamped = Math.min(100, Math.max(15, per_page ?? 15));

    const params: Record<string, unknown> = {
      page,
      per_page: perPageClamped,
      'sorting[column]': sort_column,
      'sorting[direction]': sort_direction,
    };

    // Búsqueda por referencia externa: API solo permite in, not_in para external_order_ref
    const allFilters: {
      column: string;
      operator: string;
      value?: string | string[];
    }[] = [];

    if (searchTerm) {
      allFilters.push({
        column: 'external_order_ref',
        operator: 'in',
        value: [searchTerm.trim()],
      });
    }

    if (advanced_filters?.filters) {
      allFilters.push(...advanced_filters.filters);
    }

    if (allFilters.length > 0) {
      allFilters.forEach((filter, index) => {
        params[`advanced_filters[filters][${index}][column]`] = filter.column;
        params[`advanced_filters[filters][${index}][operator]`] = filter.operator;
        if (filter.operator === 'is_null' || filter.operator === 'is_not_null') {
          // No enviar value
        } else if (
          filter.operator === 'in' ||
          filter.operator === 'not_in'
        ) {
          const val = filter.value;
          if (Array.isArray(val)) {
            val.forEach((v, valIndex) => {
              params[
                `advanced_filters[filters][${index}][value][${valIndex}]`
              ] = v;
            });
          } else if (val !== undefined) {
            params[`advanced_filters[filters][${index}][value][0]`] = val;
          }
        } else if (filter.value !== undefined) {
          params[`advanced_filters[filters][${index}][value]`] = filter.value;
        }
      });
      params['advanced_filters[logic]'] = advanced_filters?.logic || 'AND';
    }

    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<{
      data: ImportHistoryItem[];
      meta: ImportHistoryResponseMeta;
    }>(
      '/shops/import-history',
      params,
      {},
      showGlobalLoading
    );

    const data = response.data ?? [];
    const rawMeta = response.meta;
    const currentPage =
      rawMeta?.current_page ?? rawMeta?.currentPage ?? 1;
    const lastPage = rawMeta?.last_page ?? rawMeta?.lastPage ?? 1;
    const total = rawMeta?.total ?? 0;

    return {
      items: Array.isArray(data) ? data : [],
      nextPage: currentPage < lastPage ? currentPage + 1 : undefined,
      hasMore: currentPage < lastPage,
      total,
    };
  }

  /**
   * Obtiene el detalle de una importación.
   * GET /api/v1/shops/import-history/{id}
   */
  async getImportHistoryDetail(id: number): Promise<ImportHistoryDetail> {
    const response = await this.httpClient.get<{ data: ImportHistoryDetail }>(
      `/shops/import-history/${id}`
    );
    return (response as any)?.data ?? response;
  }

  /**
   * Exporta el histórico a Excel.
   * POST /api/v1/shops/import-history/export
   * Body JSON: sorting (opcional), advanced_filters (opcional).
   * Respuesta: excel_path o { file: { url } } → se devuelve la URL.
   */
  async exportToExcel(filters: ImportHistoryFilters = {}): Promise<string> {
    const {
      sort_column = 'id',
      sort_direction = 'desc',
      searchTerm = '',
      advanced_filters,
    } = filters;

    const body: {
      sorting?: { column: string; direction: string };
      advanced_filters?: {
        logic: string;
        filters: Array<{
          column: string;
          operator: string;
          value?: string | string[] | number[];
        }>;
      };
    } = {};

    body.sorting = {
      column: sort_column,
      direction: sort_direction,
    };

    const allFilters: Array<{
      column: string;
      operator: string;
      value?: string | string[];
    }> = [];

    if (searchTerm) {
      allFilters.push({
        column: 'external_order_ref',
        operator: 'in',
        value: [searchTerm.trim()],
      });
    }

    if (advanced_filters?.filters?.length) {
      allFilters.push(...advanced_filters.filters);
    }

    if (allFilters.length > 0) {
      body.advanced_filters = {
        logic: advanced_filters?.logic || 'AND',
        filters: allFilters.map((filter) => {
          const item: {
            column: string;
            operator: string;
            value?: string | string[] | number[];
          } = {
            column: filter.column,
            operator: filter.operator,
          };
          if (
            filter.operator !== 'is_null' &&
            filter.operator !== 'is_not_null' &&
            filter.value !== undefined
          ) {
            if (
              filter.operator === 'in' ||
              filter.operator === 'not_in'
            ) {
              item.value = Array.isArray(filter.value)
                ? filter.value
                : [filter.value];
            } else {
              item.value = Array.isArray(filter.value)
                ? filter.value[0]
                : filter.value;
            }
          }
          return item;
        }),
      };
    }

    const response = await this.httpClient.post<{
      excel_path?: string;
      file?: { name?: string; url?: string };
      url_file?: string;
    }>('/shops/import-history/export', body);

    const url =
      response?.excel_path ??
      response?.file?.url ??
      response?.url_file;
    if (!url) {
      throw new Error(
        'La respuesta de exportación no contiene excel_path, file.url ni url_file'
      );
    }
    return url;
  }

  /**
   * Reintenta la importación de un registro fallido.
   * POST /api/v1/orders/import/retry/{id}
   */
  async retryImport(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.httpClient.post<{ success: boolean; message: string }>(
        `/orders/import/retry/${id}`,
        {}
      );
      return {
        success: response.success ?? true,
        message: response.message || 'Reintento disparado correctamente',
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error al reintentar la importación',
      };
    }
  }

  /**
   * Reintenta la importación de múltiples registros fallidos.
   * POST /api/v1/orders/import/retry-batch
   */
  async retryImportBatch(ids: number[]): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.httpClient.post<{ success: boolean; message: string }>(
        '/orders/import/retry-batch',
        { order_import_history_ids: ids }
      );
      return {
        success: response.success ?? true,
        message: response.message || 'Reintento masivo disparado correctamente',
      };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Error al reintentar la importación masiva',
      };
    }
  }
}
